import Group from '@models/Group';
import Collection from '@models/Collection';
import Occasion from '@support/Occasion';
import type { ID, IDRecord, ReduxAction } from '@app/types';
import { OrderedMap, Iterable } from 'immutable';
import { searchResponseToContactIDs } from './search/createSearchReducer';

export interface GroupsState {
  collection: Collection<Group>;
  newGroup: Group;
  droppedIntoGroup: ID | null;
}

const initialGroupsState = {
  collection: new Collection<Group>(),
  newGroup: new Group(),
  droppedIntoGroup: null,
};

// TODO: handle failures

export function groupsReducer(
  state: GroupsState = initialGroupsState,
  { type, payload }: ReduxAction,
) {
  switch (type) {
    case 'LOAD_LOCAL_STORE_SUCCEEDED':
      const locallyStoredGroups = payload.get('groups');
      const locallyStoredGroupSorting = payload.get('group_sorting');
      let nextState = state;

      if (locallyStoredGroups && !state.collection.updated) {
        nextState = {
          ...state,
          collection: state.collection.copyWith({
            items: locallyStoredGroups.map((group) => {
              // We could have already loaded a single group via a GET request, which
              // we would want to prefer over whatever has been cached in local storage
              const groupInStore = state.collection.items.get(String(group.id));

              return [
                group.slug || group.id,
                groupInStore
                  ? groupInStore.copyWith(group)
                  : new Group({ ...group, updated: new Occasion() }),
              ];
            }),
          }),
        };
      }

      if (locallyStoredGroupSorting) {
        nextState = {
          ...nextState,
          collection: nextState.collection.copyWith({
            items: nextState.collection.items.mapEntries(([groupID, group]) => {
              const sorting = locallyStoredGroupSorting[groupID];
              if (sorting) {
                return [groupID, group.copyWith({ sorting })];
              }
              return [groupID, group];
            }),
          }),
        };
      }

      return nextState;

    case 'FETCH_ALL_GROUPS_REQUESTED':
      return {
        ...state,
        collection: state.collection.copyWith({ isFetching: true, error: null }),
      };

    case 'FETCH_ALL_GROUPS_SUCCEEDED':
      return {
        ...state,
        collection: state.collection.copyWith({
          updated: new Occasion(),
          isFetching: false,
          items: OrderedMap(
            payload.response?.map((group: any) => {
              const groupInStore = state.collection.items.get(String(group.slug || group.id));
              const groupToCopy = groupInStore?.updated ? groupInStore : new Group({});
              const omitGroupUpdate = payload.omitGroupID == String(group.id);
              const updatedGroup = omitGroupUpdate
                ? groupToCopy
                : groupToCopy.copyWith({
                    ...group,
                    updated: new Occasion(),
                  });

              return [String(group.slug || group.id), updatedGroup];
            }),
          ),
        }),
      };

    case 'FETCH_ALL_GROUPS_FAILED':
      return {
        ...state,
        collection: state.collection.copyWith({ isFetching: false, error: payload }),
      };

    case 'FETCH_GROUP_REQUESTED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            payload.groupID,
            new Group({ isFetching: true }),
            (group: Group) => group.copyWith({ isFetching: true, error: null }),
          ),
          error: null,
        }),
      };

    case 'FETCH_GROUP_SUCCEEDED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(payload.slug || payload.groupID, (group: Group) => {
            const isFirstPage = payload.page === 1;
            let items: [ID, IDRecord][] | Iterable<ID, IDRecord> = searchResponseToContactIDs(
              payload.response,
            );

            if (!isFirstPage) {
              items = group.items.concat(items);
            }

            // TODO: do we still need this?
            // let markAsUpdated = true;

            // When we refresh group data on app visibility, we only want to mark the group
            // as having been updated if the server data is different from what we have in the
            // store. This lets us sensibly handle updates in a group view that is currently
            // being edited:
            //   - If user is editing and the group data has updated on the server, we mark the group as
            //     `updated`, which will cancel editing and trash any unsaved changes. * This signals that
            //     the user has saved the group elsewhere, and we treat server as source of truth
            //   - If user is editing and the group data has not updated on the server, we don't mark the
            //     group as having updated. This ensures that the editing session state changes are kept intact.

            // if (payload.isBackgroundSync) {
            //   const newGroup = new Group(payload.response);
            // markAsUpdated = group.isDifferent(newGroup);
            // }

            const updatedGroup = group.copyWith({
              isFetching: false,
              // updated: markAsUpdated ? new Occasion() : group.updated,
              // how to handle deleted and/or merged contacts?
              items,
              updated: new Occasion(),
              persistedItemsOnServer: payload.response?.hits?.total,
              page: payload.page,
            });

            if (
              (!updatedGroup.allContactsLoaded && !updatedGroup.canLoadMore) ||
              // TODO: Hack warning. We need to fix pagination in the Search API in order to fix this.
              //       The plan is to try using search_after instead of our current offset-based pagination.
              //       See: https://www.elastic.co/guide/en/elasticsearch/reference/current/paginate-search-results.html#search-after
              payload.response.hits.hits.length === 0
            ) {
              return updatedGroup.copyWith({ allContactsLoaded: true });
            }

            return updatedGroup;
          }),
        }),
      };

    case 'FETCH_GROUP_FAILED':
      if (state.collection.items.has(payload.requestID)) {
        if (payload.status == 404) {
          return {
            ...state,
            collection: state.collection.copyWith({
              items: state.collection.items.delete(payload.requestID),
              error: payload,
            }),
          };
        } else {
          return {
            ...state,
            collection: state.collection.copyWith({
              items: state.collection.items.update(payload.requestID, (group: Group) =>
                group.copyWith({
                  isFetching: false,
                  updated: new Occasion(),
                  error: payload,
                }),
              ),
            }),
          };
        }
      } else {
        return {
          ...state,
          collection: state.collection.copyWith({ error: payload }),
        };
      }

    case 'UPDATE_GROUP_REQUESTED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(payload.slug || payload.id, (group: Group) =>
            group.copyWith({ ...payload, isFetching: true, error: null }),
          ),
        }),
      };

    case 'UPDATE_GROUP_SUCCEEDED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            payload.response.slug || String(payload.response.id),
            (group: Group) =>
              group.copyWith({
                ...payload.response,
                isFetching: false,
                updated: new Occasion(),
              }),
          ),
          updated: new Occasion(),
        }),
      };

    case 'UPDATE_GROUP_FAILED':
      const preErrorGroupState = payload.preErrorGroupState;

      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.set(
            payload.groupKey,
            preErrorGroupState.copyWith({ error: true, updated: new Occasion() }),
          ),
        }),
      };

    case 'UPDATE_GROUPS_IN_BULK_REQUESTED':
      // add/remove contact IDs from the specified groups
      // don't do anything to handle new groups

      let groupsWithChanges = state.collection.items;
      for (const groupID of payload.add_group_ids || []) {
        groupsWithChanges = groupsWithChanges.update(groupID, (group: Group) => {
          if (group) {
            return group.addContactIDs(payload.contact_ids);
          }
          return group;
        });
      }

      for (const groupID of payload.remove_group_ids || []) {
        groupsWithChanges = groupsWithChanges.update(groupID, (group: Group) => {
          if (group) {
            return group.removeContactGroupByIDs(payload.contact_ids);
          }
          return group;
        });
      }

      return {
        ...state,
        collection: state.collection.copyWith({
          items: groupsWithChanges,
        }),
      };

    case 'UPDATE_GROUPS_IN_BULK_SUCCEEDED':
      const addedGroups = payload.response?.map((group) => {
        return [
          String(group.slug || group.id),
          new Group({
            ...group,
            contact_ids: payload.originalPayload.contact_ids,
            updated: new Occasion(),
          }),
        ];
      });

      return {
        ...state,
        collection: state.collection.copyWith({
          items: OrderedMap(addedGroups).concat(state.collection.items),
          updated: new Occasion(),
        }),
      };

    case 'UPDATE_GROUPS_IN_BULK_FAILED':
      // TODO: Alert and reverse changes
      return state;

    case 'SET_GROUP_SORTING':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.update(payload.groupID, (group: Group) =>
            group.copyWith({ sorting: payload.sorting }),
          ),
        }),
      };

    case 'CLEAR_NEW_GROUP':
      return {
        ...state,
        newGroup: new Group(),
      };

    case 'CREATE_GROUP_REQUESTED':
      return {
        ...state,
        newGroup: state.newGroup.copyWith({ isFetching: true }),
        collection: state.collection.copyWith({
          items: OrderedMap([[payload.id, payload.copyWith({ updated: new Occasion() })]])
            .merge(state.collection.items)
            .sort((a: Group) => (a.is_internal ? -1 : 0)),
        }),
      };

    case 'CREATE_GROUP_SUCCEEDED':
      return {
        ...state,
        newGroup: new Group(payload.response),
        collection: state.collection.copyWith({
          items: state.collection.items.mapEntries(([k, v]) => {
            if (k == payload.tempID) {
              return [
                payload.response.id.toString(),
                new Group({
                  ...payload.response,
                  updated: new Occasion(),
                  items: OrderedMap((payload.response.contact_ids || []).map((id) => [id, { id }])),
                }),
              ];
            }
            return [k, v];
          }),
        }),
      };

    case 'DELETE_GROUP_REQUESTED':
      return {
        ...state,
        newGroup: new Group(),
        collection: state.collection.copyWith({
          items: state.collection.items.delete(payload.id),
        }),
      };

    case 'DROP_INTO_GROUP':
      return {
        ...state,
        droppedIntoGroup: payload.id,
      };

    case 'CLEAR_DROPPED_INTO_GROUP':
      return {
        ...state,
        droppedIntoGroup: null,
      };

    case 'UPDATE_GROUP_ORDER':
      const internalGroups = state.collection.items
        .filter(({ is_internal }) => is_internal)
        .toArray()
        .map((group) => [group.slug || group.id, group]);
      const sortedGroups = payload.newGroupOrder.map((id) => [id, state.collection.items.get(id)]);

      return {
        ...state,
        collection: state.collection.copyWith({
          items: OrderedMap(internalGroups.concat(sortedGroups)),
        }),
      };

    case 'DELETE_CONTACT_REQUESTED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.mapEntries(([groupID, group]) => {
            if (group.hasContact(payload.id)) {
              return [groupID, group.removeContact(payload.id)];
            }
            return [groupID, group];
          }),
        }),
      };

    case 'DELETE_CONTACT_BATCH_REQUESTED':
      return {
        ...state,
        collection: state.collection.copyWith({
          items: state.collection.items.mapEntries(([groupID, group]) => [
            groupID,
            group.removeContactGroupByIDs(payload.contact_ids),
          ]),
        }),
      };

    // case ''

    default:
      return state;
  }
}
